import { $, html, View } from 'rune-ts';
import { createCreatorProductStore } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/createStore.ts';
import { formKeyMap } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import { Input_NumberEditor } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_NumberEditor.ts';
import { BaseSection } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/BaseSection.ts';
import { Input_Date } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Date.ts';
import { ChangeCalendarEvent } from '@services/MarppleShop/marppleShopStudioApp/components/cells/StudioCalendar/StudioCalendar.ts';
import {
  DeleteDateEvent,
  SelectCheckBoxEvent,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/events.ts';
import { compact, each, entries, filter, pipe, toArray } from '@fxts/core';
import { addMinutes, subMinutes } from 'date-fns';
import {
  formatDateField,
  subscribeStore,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/helper.ts';
import { Input_CheckBoxes } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_CheckBoxes.ts';
import { MODE } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import klass from './styles/badge.module.scss';
import color_klass from './styles/color.module.scss';
import { classes } from '@services/MarppleShop/shared/util';
import { isNessApp } from '@modules/Util/S/Function/service_app_name';
import { shareFont } from '@services/MarppleShop/shared/typography';
import { Input_TextEditor } from '@services/MarppleShopStudio/shared/component/atom/Input/Input_TextEditor.ts';
import common_klass from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/styles/common.module.scss';
import { Tooltip } from '@services/MarppleShop/renderApp/components/atoms/Tooltip/Tooltip.ts';
import { unEscaper } from '@shared/utils/escape.ts';
import { Input_Toggles } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Toggles.ts';
import { adultIcon } from '@services/MarppleShop/shared/components/atoms/Icon/foundation_icons.ts';

interface SalesInfoSectionData<T extends MODE> {
  store: ReturnType<typeof createCreatorProductStore<T>>;
  is_created_sku?: boolean;
}

const BADGE_ID_MAP = {
  LIMIT: 1,
  ADULT: 2,
};

export class SalesInfoSection<T extends MODE> extends BaseSection<T, SalesInfoSectionData<T>> {
  protected readonly sectionTitle = '판매 정보';
  private store = this.data.store;
  private inputMap = {
    [formKeyMap.is_force_out_of_stock]: new Input_CheckBoxes({
      name: formKeyMap.is_force_out_of_stock,
      value: [{ id: 1, label: '품절 상태로 표시합니다.', active: this.store.formData.is_force_out_of_stock }],
      title: '매진 설정',
    }),
    [formKeyMap.sell_start_at]: new Input_Date({
      name: formKeyMap.sell_start_at,
      value: formatDateField(this.store.formData.sell_start_at),
      placeholder: '즉시 판매',
      title: '판매 시작일시',
    }),
    [formKeyMap.sell_end_at]: new Input_Date({
      name: formKeyMap.sell_end_at,
      value: formatDateField(this.store.formData.sell_end_at),
      placeholder: '상시 판매',
      title: html`
        <div class="${common_klass.title_with_tooltip}">
          <span class="${common_klass.text} ${shareFont('14_bold')}">판매 종료일시</span>
          ${new Tooltip({
            id: formKeyMap.sell_end_at,
            content: html`
              <div class="${common_klass.tooltip_content_container} ${shareFont('14_medium')}">
                <span
                  >종료까지 남은 시간이 7일(168시간) 이내일 경우, 상품 페이지에 남은 시간을 보여주는
                  카운트다운이 표시됩니다.</span
                >
              </div>
            `,
            size: 'x_small',
            variant: 'studio',
          })}
        </div>
      `,
    }),
    [formKeyMap.max_purchase_per_user]: new Input_NumberEditor({
      name: formKeyMap.max_purchase_per_user,
      value: this.store.formData.max_purchase_per_user ?? null,
      title: '인당 최대구매수량',
      option: {
        placeholder: '한 계정으로 구매할 수 있는 최대 수량을 입력해 주세요.',
        right_html: html`<div class="${color_klass.gray_80} ${shareFont('14_medium')}">개</div>`,
      },
      onFocusOut: (view) => {
        this.store.actions.setField(formKeyMap.max_purchase_per_user, view.getNumericValue());
      },
    }),
    [formKeyMap.meta]: new Input_Toggles({
      name: formKeyMap.meta,
      value: isNessApp()
        ? [
            {
              id: BADGE_ID_MAP.LIMIT,
              label: html`<div class="${klass.badge_label_wrap}">
                <div class="${classes(klass.badge, shareFont('12_medium'), klass.ness)}">Special Offer</div>
                <div class="${shareFont('14_medium')}">뱃지를 표시합니다.</div>
                ${new Tooltip({
                  id: formKeyMap.limited,
                  content: html`
                    <div class="${common_klass.tooltip_content_container} ${shareFont('14_medium')}">
                      <span
                        >상품 공개 후 판매 종료일시 또는 재고를 변경할 시 주의해주세요. 한정판이 아닌 상품으로
                        해석될 수 있습니다.</span
                      >
                    </div>
                  `,
                  size: 'x_small',
                  variant: 'studio',
                })}
              </div>`,
              active: this.store.formData.meta.limited,
              onClick: (view, event) => {
                event.preventDefault();
                const setting = !view.getSetting();
                this.store.actions.setField(formKeyMap.limited, setting);
                view.setSetting(setting);
              },
            },
          ]
        : [
            {
              id: BADGE_ID_MAP.ADULT,
              label: html`
                <div class="${klass.badge_label_wrap}">
                  ${adultIcon()}
                  <div class="${shareFont('14_medium')}">상품으로 성인인증이 필요합니다.</div>
                  ${new Tooltip({
                    id: formKeyMap.adult,
                    content: html`
                      <div class="${common_klass.tooltip_content_container} ${shareFont('14_medium')}">
                        <span>성인 인증 없이 등록된 성인 상품에 대한 책임은 판매자에게 있습니다.</span>
                      </div>
                    `,
                    size: 'x_small',
                    variant: 'studio',
                  })}
                </div>
              `,
              active: this.store.formData.meta.adult,
              disabled: Boolean($('html[need_adult_check="true"]')),
              onClick: (view, event) => {
                event.preventDefault();
                const setting = !view.getSetting();
                this.store.actions.setField(formKeyMap.adult, setting);
                view.setSetting(setting);
              },
            },
            {
              id: BADGE_ID_MAP.LIMIT,
              label: html`<div class="${klass.badge_label_wrap}">
                <div class="${classes(klass.badge, shareFont('12_medium'))}">한정</div>
                <div class="${shareFont('14_medium')}">뱃지를 표시합니다.</div>
                ${new Tooltip({
                  id: formKeyMap.limited,
                  content: html`
                    <div class="${common_klass.tooltip_content_container} ${shareFont('14_medium')}">
                      <span
                        >상품 공개 후 판매 종료일시 또는 재고를 변경할 시 주의해주세요. 한정판이 아닌 상품으로
                        해석될 수 있습니다.</span
                      >
                    </div>
                  `,
                  size: 'x_small',
                  variant: 'studio',
                })}
              </div>`,
              active: this.store.formData.meta.limited,
              onClick: (view, event) => {
                event.preventDefault();
                const setting = !view.getSetting();
                this.store.actions.setField(formKeyMap.limited, setting);
                view.setSetting(setting);
              },
            },
          ],
      title: html`
        <div class="${common_klass.title_with_tooltip}">
          <span class="${common_klass.text} ${shareFont('14_bold')}">뱃지 설정</span>
        </div>
      `,
    }),
  };

  private fundingGoalInput = new Input_NumberEditor({
    name: formKeyMap.target_amount,
    value: this.store.formData.meta?.funding?.target_amount ?? null,
    title: html`
      <div class="${common_klass.title_with_tooltip}">
        <span class="${common_klass.text} ${shareFont('14_bold')}">목표 판매금액</span>
        ${new Tooltip({
          id: formKeyMap.target_amount,
          content: html`
            <div class="${common_klass.tooltip_content_container} ${shareFont('14_medium')}">
              <div class="${common_klass.tooltip_content_divider}">
                <span>목표 판매금액을 설정하면 상품 상세에 누적 판매 금액과 목표 금액이 노출됩니다.</span>
              </div>
            </div>
          `,
          size: 'x_small',
          variant: 'studio',
        })}
      </div>
    `,
    option: {
      placeholder: '목표 판매금액을 입력해 주세요.',
      min: 0,
      right_html: html`<div class="${color_klass.gray_80} ${shareFont('14_medium')}">원</div>`,
    },
    onFocusOut: (view) => {
      this.store.actions.setField(formKeyMap.target_amount, view.getNumericValue());
    },
  });

  private fundingDescriptionInput = new Input_TextEditor({
    name: formKeyMap.description,
    value: this.store.formData.meta?.funding?.description
      ? unEscaper(this.store.formData.meta.funding.description)
      : '',
    title: '챌린지 설명',
    option: {
      placeholder: '챌린지에 대한 설명을 입력해 주세요.',
      max_length: 80,
      trim: true,
    },
    onFocusout: (view) => {
      this.store.actions.setField(formKeyMap.description, view.getValue());
    },
  });

  public override inputViews(): Array<View> {
    return pipe(
      [
        this.inputMap.is_force_out_of_stock,
        this.inputMap.sell_start_at,
        this.inputMap.sell_end_at,
        this.inputMap.max_purchase_per_user,
        !isNessApp() && (this.store.mode === MODE.EDIT_TPL || this.store.mode === MODE.EDIT_TPL_INTL)
          ? this.fundingGoalInput
          : null,
        !isNessApp() && (this.store.mode === MODE.EDIT_TPL || this.store.mode === MODE.EDIT_TPL_INTL)
          ? this.fundingDescriptionInput
          : null,
        this.inputMap.meta,
      ],
      compact,
      toArray,
    );
  }

  protected override onRender() {
    this.handleSellStartAt(this.store.formData.sell_start_at);
    this.handleSellEndAt(this.store.formData.sell_end_at);
    this.setDateViewEvent();
    this.setDateViewSubscribe();

    subscribeStore(this.store, {
      ...this.inputMap,
      [formKeyMap.target_amount]: this.fundingGoalInput,
    });
  }

  private setDateViewEvent() {
    pipe(
      this.inputMap,
      entries,
      filter(
        (entry): entry is [typeof formKeyMap.sell_start_at | typeof formKeyMap.sell_end_at, Input_Date] => {
          const [key] = entry;
          return key === formKeyMap.sell_start_at || key === formKeyMap.sell_end_at;
        },
      ),
      each(([name, view]) => {
        view
          .addEventListener(ChangeCalendarEvent, (e) => {
            this.store.actions.setField(name, e.detail.start_date);
          })
          .addEventListener(DeleteDateEvent, () => {
            this.store.actions.setField(name, null);
          });
      }),
    );

    this.addEventListener(SelectCheckBoxEvent, (e) => {
      switch (e.detail.name) {
        case formKeyMap.meta: {
          this.store.actions.setField(
            formKeyMap.limited,
            !!e.detail.checkbox_ids.find((id) => id === BADGE_ID_MAP.LIMIT),
          );
          break;
        }
        case formKeyMap.is_force_out_of_stock: {
          this.store.actions.setField(formKeyMap.is_force_out_of_stock, !!e.detail.checkbox_ids?.length);
          break;
        }
      }
    });
  }

  private setDateViewSubscribe() {
    this.store.subscribeState(
      (state) => state.formData.sell_start_at,
      (sell_start_at) => this.handleSellStartAt(sell_start_at),
    );

    this.store.subscribeState(
      (state) => state.formData.sell_end_at,
      (sell_end_at) => this.handleSellEndAt(sell_end_at),
    );
  }

  private handleSellStartAt(sell_start_at: Date | undefined | null) {
    this.inputMap.sell_start_at.setDate(sell_start_at);
    // 시작일에서 1분을 더한 값을 시작일의 최대값으로 설정
    const minEndDate = sell_start_at ? addMinutes(sell_start_at, 1) : sell_start_at;
    this.inputMap.sell_end_at.setMinDate(minEndDate);
  }

  private handleSellEndAt(sell_end_at: Date | undefined | null) {
    this.inputMap.sell_end_at.setDate(sell_end_at);
    // 종료일에서 1분을 뺀 값을 시작일의 최대값으로 설정
    const maxStartDate = sell_end_at ? subMinutes(sell_end_at, 1) : sell_end_at;
    this.inputMap.sell_start_at.setMaxDate(maxStartDate);
  }
}
